import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  GitBranch, Copy, Download, Search, Loader2, Check,
  ExternalLink, FileCode, Terminal
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platformIcons = {
  "GitHub Actions": "github",
  "GitLab CI": "gitlab",
  "ArgoCD": "argocd",
  "Jenkins": "jenkins"
};

const categoryColors = {
  container: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  infrastructure: "bg-green-500/10 text-green-400 border-green-500/30",
  kubernetes: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  gitops: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  enterprise: "bg-red-500/10 text-red-400 border-red-500/30",
  serverless: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30"
};

const complexityColors = {
  low: "bg-[#00ff9d]/10 text-[#00ff9d] border-[#00ff9d]/30",
  medium: "bg-[#00f0ff]/10 text-[#00f0ff] border-[#00f0ff]/30",
  high: "bg-[#ff6b00]/10 text-[#ff6b00] border-[#ff6b00]/30"
};

export const CICDTemplates = ({ onSelectTemplate, className = "" }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateDetail, setTemplateDetail] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cicd-templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error("Failed to load CI/CD templates");
    } finally {
      setLoading(false);
    }
  };

  const loadTemplateDetail = async (templateId) => {
    try {
      const response = await axios.get(`${API}/cicd-templates/${templateId}`);
      setTemplateDetail(response.data);
      setSelectedTemplate(templateId);
    } catch (error) {
      toast.error("Failed to load template details");
    }
  };

  const handleCopy = () => {
    if (!templateDetail?.template) return;
    navigator.clipboard.writeText(templateDetail.template);
    setCopied(true);
    toast.success("Template copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!templateDetail?.template) return;
    const blob = new Blob([templateDetail.template], { type: "text/yaml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = templateDetail.file_path.split("/").pop();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Template downloaded!");
  };

  const filteredTemplates = templates.filter((template) => {
    const matchesSearch = 
      template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      template.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlatform = selectedPlatform === "all" || template.platform === selectedPlatform;
    return matchesSearch && matchesPlatform;
  });

  const platforms = ["all", ...new Set(templates.map((t) => t.platform))];

  if (loading) {
    return (
      <Card className={`bg-[#09090b] border-white/5 ${className}`}>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-[#00f0ff] animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={`bg-[#09090b] border-white/5 ${className}`} data-testid="cicd-templates">
        <CardHeader className="pb-4">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-[#00f0ff]" />
            CI/CD Pipeline Templates
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search pipelines..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-black/50 border-white/10 text-white focus:border-[#00f0ff]/50 h-9"
                data-testid="cicd-search-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {platforms.map((platform) => (
                <Button
                  key={platform}
                  variant={selectedPlatform === platform ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedPlatform(platform)}
                  className={
                    selectedPlatform === platform
                      ? "bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90 h-8"
                      : "border-zinc-700 text-zinc-300 hover:bg-white/5 h-8"
                  }
                  data-testid={`platform-filter-${platform.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {platform === "all" ? "All" : platform}
                </Button>
              ))}
            </div>
          </div>

          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredTemplates.map((template) => (
              <Card
                key={template.id}
                className="bg-black/30 border-white/5 hover:border-[#00f0ff]/30 transition-all cursor-pointer group"
                onClick={() => loadTemplateDetail(template.id)}
                data-testid={`cicd-template-${template.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-sm bg-zinc-900 border border-zinc-800 flex items-center justify-center">
                      <FileCode className="w-5 h-5 text-[#00f0ff]" />
                    </div>
                    <div className="flex gap-2">
                      <Badge variant="outline" className={complexityColors[template.complexity]}>
                        {template.complexity}
                      </Badge>
                    </div>
                  </div>
                  
                  <h3 className="text-sm font-semibold text-white mb-1 group-hover:text-[#00f0ff] transition-colors">
                    {template.name}
                  </h3>
                  <p className="text-xs text-zinc-500 mb-3 line-clamp-2">
                    {template.description}
                  </p>
                  
                  <div className="flex items-center justify-between pt-3 border-t border-white/5">
                    <Badge variant="outline" className={categoryColors[template.category]}>
                      {template.category}
                    </Badge>
                    <span className="text-xs text-zinc-600">{template.platform}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="text-center py-8">
              <p className="text-zinc-500">No templates match your search</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Detail Dialog */}
      <Dialog open={!!selectedTemplate} onOpenChange={() => setSelectedTemplate(null)}>
        <DialogContent className="max-w-3xl bg-[#09090b] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Terminal className="w-5 h-5 text-[#00f0ff]" />
              {templateDetail?.name}
            </DialogTitle>
          </DialogHeader>
          
          {templateDetail && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm">
                <span className="text-zinc-500">Platform:</span>
                <Badge variant="outline" className="border-[#00f0ff]/30 text-[#00f0ff]">
                  {templateDetail.platform}
                </Badge>
                <span className="text-zinc-500">File:</span>
                <code className="text-xs bg-zinc-900 px-2 py-1 rounded text-zinc-300">
                  {templateDetail.file_path}
                </code>
              </div>
              
              <p className="text-sm text-zinc-400">{templateDetail.description}</p>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleCopy}
                  className="bg-[#00f0ff] text-black hover:bg-[#00f0ff]/90"
                  size="sm"
                >
                  {copied ? (
                    <Check className="w-4 h-4 mr-2" />
                  ) : (
                    <Copy className="w-4 h-4 mr-2" />
                  )}
                  {copied ? "Copied!" : "Copy"}
                </Button>
                <Button
                  onClick={handleDownload}
                  variant="outline"
                  className="border-zinc-700 text-white hover:bg-white/5"
                  size="sm"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                {onSelectTemplate && (
                  <Button
                    onClick={() => {
                      onSelectTemplate(templateDetail);
                      setSelectedTemplate(null);
                    }}
                    variant="outline"
                    className="border-[#00f0ff]/30 text-[#00f0ff] hover:bg-[#00f0ff]/10"
                    size="sm"
                  >
                    Use Template
                  </Button>
                )}
              </div>
              
              <ScrollArea className="h-[400px] border border-white/10 rounded-sm">
                <pre className="p-4 text-xs text-zinc-300 font-mono whitespace-pre-wrap">
                  {templateDetail.template}
                </pre>
              </ScrollArea>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
